import styled from '@emotion/styled'
import cx from 'classnames'

import { ButtonLink } from '@/legacy/components/Button'
import { Card } from '@/legacy/components/Card'
import { Container, type BackgroundColor, type Space } from '@/legacy/components/Container'
import { Countdown } from '@/legacy/components/Countdown'
import type { HasCountdownProps } from '@/legacy/components/Countdown/types'
import { Image } from '@/legacy/components/Image'
import { RichText } from '@/legacy/components/RichText'
import {
  BodyM,
  Display,
  HeadingS,
  type TypographyColorModifier,
} from '@/legacy/components/Typography'
import { parseIsoToTimestampMS } from '@/legacy/core/utils/date/parseIsoToTimestampMS'
import { decodeURL } from '@/legacy/core/utils/dom/decodeURL'
import type { TitleProps } from '@/legacy/models'
import type { BuilderImage } from '@/legacy/models/BuilderImage'
import { CONFIG } from '@/ui/shared/styles'

import { builderLightText } from '../utils'

import { withDebtsPartnerDiscount } from './hocs/withDebtsPartnerDiscount'

interface CtaButton {
  label: string
  link: string
  rounded: boolean
  buttonAriaLabel?: string
}

interface Cta {
  title: string
  button: CtaButton
}

interface Hero {
  title: TitleProps
  subtitle: TitleProps
  colorModifier?: TypographyColorModifier
}

export interface HeroWithCtaProps extends HasCountdownProps {
  hero: Hero
  cta: Cta
  image?: BuilderImage
  background?: BackgroundColor
  backgroundColor?: string
  spaceOut?: Space
}

const HeroWithCtaStyles = styled.div`
  .hero-content {
    display: flex;
    position: relative;
    padding: 0rem 1rem;
    padding-top: 5rem;
    width: 100%;
    margin: 0 auto;
    max-width: 960px;
  }

  .hero-with-cta__content {
    padding-bottom: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-around;
  }

  .hero-with-cta__content__title {
    max-width: 26.875rem;
    margin-bottom: 1rem;
  }

  .hero-with-cta__content__subtitle {
    word-wrap: break-word;
    flex-wrap: wrap;
    overflow: hidden;
    max-width: 23.875rem;
  }

  .hero-with-cta__content__cta {
    margin-top: 2.5rem;
    border: ${CONFIG.theme.borders.lightGray};

    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;

    width: 19.5rem;

    a {
      width: 16.5rem;
    }
  }

  .hero-with-cta__content__cta__title {
    margin-bottom: 1rem;
    text-align: center;
    min-width: 16.5rem;
  }

  .hero-with-cta__image-container {
    width: 31.25rem;
    height: 31.25rem;
    margin-top: auto;
  }

  ${CONFIG.media.mobileLg} {
    .hero-with-cta__content__title,
    .hero-with-cta__content__subtitle {
      max-width: unset;
    }

    .hero-content {
      flex-direction: column;
      align-items: center;
      padding-bottom: 10rem;
      padding-top: 2rem;
    }

    .hero-with-cta__image-container {
      width: 15.625rem;
      height: 15.625rem;
    }

    .hero-with-cta__content__cta {
      position: absolute;
      bottom: 0;
      z-index: 2;
      margin-bottom: 1.5rem;
    }

    .hero-with-cta__content__cta__container {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  ${CONFIG.media.mobileSm} {
    .hero-with-cta__image-container {
      width: 10.625rem;
      height: 10.625rem;
    }

    .hero-with-cta__content__cta {
      margin-bottom: 0.5rem;
    }
  }
`

const HeroWithCtaBlock = ({
  hero,
  cta,
  image,
  background,
  backgroundColor,
  spaceOut,
  hasCountdown,
  clockBackGround,
  clockBackGroundHex,
  labelColorModifier,
  clockColorModifier,
  countdownDate,
  countdownDay,
}: HeroWithCtaProps) => {
  const lightText = builderLightText(hero && hero.colorModifier)

  const countdownProps = {
    clockBackGround,
    clockBackGroundHex,
    labelColorModifier,
    clockColorModifier,
    countdownTimestampMs: parseIsoToTimestampMS(countdownDate),
    daysFromInput: countdownDay || 2,
  }

  return (
    <HeroWithCtaStyles>
      <Container
        backgroundColor={background}
        backgroundColorHex={backgroundColor}
        spaceOut={spaceOut}
        contentClassName="hero-content"
      >
        <div className="hero-with-cta__content">
          {hero && (
            <div>
              {hero.title && (
                <Display
                  className="hero-with-cta__content__title"
                  tag={hero.title.tag || 'h1'}
                  dark="high"
                  light={lightText}
                >
                  {hero.title.text}
                </Display>
              )}
              {hero.subtitle && (
                <BodyM
                  className={cx('hero-with-cta__content__subtitle', { 'eu-sm-2': hasCountdown })}
                  light={lightText}
                  tag={hero.subtitle.tag || 'h2'}
                >
                  {hero.subtitle.text}
                </BodyM>
              )}
            </div>
          )}
          {hasCountdown && countdownProps && (
            <Countdown className="e eu-sm-2" {...countdownProps} />
          )}
          {cta && (
            <div className="hero-with-cta__content__cta__container">
              <Card className="hero-with-cta__content__cta">
                {cta.title && (
                  <RichText
                    typography={HeadingS}
                    className="hero-with-cta__content__cta__title"
                    tag="div"
                    content={cta.title}
                  />
                )}
                {cta.button && (
                  <ButtonLink
                    source={decodeURL(cta.button.link)}
                    size="lg"
                    modifier="solid"
                    className="ecs-cta-btn"
                    rounded={cta.button.rounded}
                    aria-label={
                      cta.button.buttonAriaLabel ? cta.button.buttonAriaLabel : cta.button.label
                    }
                  >
                    {cta.button.label}
                  </ButtonLink>
                )}
              </Card>
            </div>
          )}
        </div>
        <div className="hero-with-cta__image-container">
          {image && (
            <Image
              className="hero-with-cta__image"
              source={image.url}
              alt={image.alt}
              width={500}
              height={500}
              aria-hidden={image['aria-hidden']}
            />
          )}
        </div>
      </Container>
    </HeroWithCtaStyles>
  )
}

export default withDebtsPartnerDiscount(HeroWithCtaBlock)
